// PHASE_1 — Bridge cookie banner ↔ Bonamassa marketing preferences.
//
// The cookie banner's "marketing" flag is about ad/tracking cookies, but for
// users that are signed in we treat an explicit decision as an opportunity to
// align Bonamassa's marketing_push_opt_in / marketing_email_opt_in to the same
// preference. This is one-way (banner → Bonamassa). The dedicated section in
// Configurações is still the authoritative UI per channel.
//
// Why this is safe:
//   - We only run on transitions made after mount (we record the initial value
//     and skip it).
//   - We only PATCH if there's an access token; anonymous users are no-op.
//   - We dedupe with a per-tab ref so re-renders don't refire.
//   - We swallow errors silently — the cookie banner is not the right surface
//     to show notification-API failures, and the user can correct from the
//     Marketing section.

import { useEffect, useRef } from "react";
import { getAccessToken, updateMarketingPreferences } from "@/app/api/auth-client";
import { useConsent } from "./consent-context";

export function MarketingConsentBridge() {
	const { state } = useConsent();
	const lastSyncedRef = useRef<boolean | null>(null);
	const initialisedRef = useRef(false);

	useEffect(() => {
		// Skip the initial render: we don't want to overwrite Bonamassa with
		// stale cookie consent data on every reload.
		if (!initialisedRef.current) {
			initialisedRef.current = true;
			lastSyncedRef.current = state.decided ? state.marketing : null;
			return;
		}
		if (!state.decided) return;
		if (lastSyncedRef.current === state.marketing) return;
		if (!getAccessToken()) return;

		const next = state.marketing;
		lastSyncedRef.current = next;

		updateMarketingPreferences({
			marketing_push_opt_in: next,
			marketing_email_opt_in: next,
		}).catch(() => {
			// Roll back our optimistic "synced" marker so a retry will fire next
			// time the user toggles.
			lastSyncedRef.current = null;
		});
	}, [state.decided, state.marketing]);

	return null;
}
